import { memo, useCallback, useMemo } from 'react';

import { useCommandState } from '@air/cmdk';

import { SearchCommandGroup } from '~/components/SearchCommand/SearchCommandGroup';
import { SearchCommandSuggestionListItem } from '~/components/SearchCommand/SearchCommandSuggestionListItem';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SearchCommandSuggestionListProps = {
  onSelect: (suggestion: string) => void;
  suggestions?: string[];
};

export const SearchCommandSuggestionList = memo(({ suggestions, onSelect }: SearchCommandSuggestionListProps) => {
  const search = useCommandState((state) => state.search);
  const { ui2026 } = useTemporaryFeatureFlags();

  const handleSelectSearch = useCallback(() => {
    onSelect(search);
  }, [onSelect, search]);

  /**
   * Check if the search is not in the suggestions list
   */
  const shouldShowSearch = useMemo(() => {
    if (ui2026) {
      return !!search;
    }

    return !!search && !suggestions?.includes(search);
  }, [search, suggestions, ui2026]);

  const suggestionRows = useMemo(() => {
    if (!suggestions?.length) {
      return [];
    }

    if (ui2026 && search) {
      return suggestions.filter((suggestion) => suggestion !== search);
    }

    return suggestions;
  }, [search, suggestions, ui2026]);

  if (!suggestionRows.length && !search) {
    return null;
  }

  return (
    <SearchCommandGroup heading={search ? undefined : 'Search suggestions'}>
      {shouldShowSearch && (
        <SearchCommandSuggestionListItem isCurrentQuery onSelect={handleSelectSearch} suggestion={search} />
      )}

      {suggestionRows.map((suggestion) => (
        <SearchCommandSuggestionListItem key={suggestion} onSelect={onSelect} suggestion={suggestion} />
      ))}
    </SearchCommandGroup>
  );
});

SearchCommandSuggestionList.displayName = 'SearchCommandSuggestionList';
